import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Play } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA } from "@/components/CTA";
import { PageMeta } from "@/components/PageMeta";
import { ArticleDiagnosticCTA } from "@/components/ArticleDiagnosticCTA";
import { useLang } from "@/i18n";
import { api } from "@/lib/insightsApi";
import { trackCta } from "@/lib/analytics";
import { ROUTES } from "@/config/links";

/**
 * Pick a localized value with EN fallback.
 */
const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || "";
};

// -----------------------------------------------------------------------------
// Public list
// -----------------------------------------------------------------------------
export function ArticlesList() {
  const { t, l, lang } = useLang();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.listArticles()
      .then((d) => setItems(d.items || []))
      .catch((e) => setErr(e.message || "Failed to load"));
  }, []);

  return (
    <>
      <PageMeta
        titleKey="meta.articles_title"
        descriptionKey="meta.articles_description"
        path={ROUTES.articles}
      />
      <PageHero
        testId="articles-hero"
        eyebrow="Insights"
        headline={["Field notes from", "the rehearsal room."]}
        emphasis="Ideas, patterns and stories on high-stakes communication."
        emphasisAccent="underline"
        intro="Written for the people who need to speak well when it matters — leaders, sponsors and L&D partners."
      />
      <section className="relative z-10 py-20 md:py-28" data-testid="articles-list-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          {err && <p className="font-body text-terracotta">{err}</p>}
          {items === null && <p className="font-body text-ink-soft italic">Loading…</p>}
          {items && items.length === 0 && (
            <p className="font-serif italic text-2xl text-ink-soft">The first articles are being written. Come back soon.</p>
          )}
          {items && items.length > 0 && (
            <ul className="grid md:grid-cols-2 gap-8 md:gap-10" data-testid="articles-grid">
              {items.map((a) => (
                <li key={a.id}>
                  <Reveal>
                    <Link
                      to={l(`/insights/articles/${a.slug}`)}
                      data-testid={`article-card-${a.slug}`}
                      onClick={() => trackCta({ ctaId: `article-card-${a.slug}`, location: "list_card", href: `/insights/articles/${a.slug}`, contentId: a.id || a.slug })}
                      className="group block bg-paper border border-ink/12 shadow-[4px_8px_24px_rgba(27,27,24,0.06)] hover:shadow-[6px_12px_36px_rgba(27,27,24,0.12)] transition-shadow duration-500"
                    >
                      {a.featured_image ? (
                        <div className="aspect-[16/10] overflow-hidden bg-paper-deep border-b border-ink/10">
                          <img src={a.featured_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-[900ms]" />
                        </div>
                      ) : (
                        <div className="aspect-[16/10] bg-paper-deep/60 border-b border-ink/10 flex items-center justify-center">
                          <span className="font-hand text-terracotta text-2xl">{a.category || "Article"}</span>
                        </div>
                      )}
                      <div className="p-7 md:p-8">
                        {a.category && <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">{a.category}</p>}
                        <h2 className="font-serif text-2xl md:text-3xl leading-tight text-ink group-hover:text-terracotta transition-colors">
                          {pick(a.title, lang)}
                        </h2>
                        {pick(a.excerpt, lang) && (
                          <p className="mt-4 font-body text-ink-soft leading-relaxed">{pick(a.excerpt, lang)}</p>
                        )}
                        <span className="mt-5 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] text-ink">
                          Read <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

// -----------------------------------------------------------------------------
// Tools — a small, static, reusable card list (not fetched from the CMS;
// each entry is a real in-site tool, starting with the Presentation
// Readiness Check). New tools get added to this one array, never a
// bespoke card system per tool.
// -----------------------------------------------------------------------------
const TOOLS = [
  {
    id: "next-presentation-readiness-check",
    titleKey: "tools.readiness_check_title",
    descriptionKey: "tools.readiness_check_description",
    to: ROUTES.nextPresentationReadinessCheck,
  },
  {
    id: "executive-presence-communication-diagnostic",
    titleKey: "tools.ep_diagnostic_title",
    descriptionKey: "tools.ep_diagnostic_description",
    to: ROUTES.executivePresenceCommunicationDiagnostic,
  },
  {
    id: "presenter-archetype",
    titleKey: "tools.archetype_title",
    descriptionKey: "tools.archetype_description",
    to: ROUTES.presenterArchetype,
  },
];

export function ToolsList() {
  const { t, l } = useLang();

  return (
    <>
      <PageMeta titleKey="meta.tools_title" descriptionKey="meta.tools_description" path={ROUTES.tools} />
      <PageHero
        testId="tools-hero"
        eyebrow="Insights"
        headline={["Practical tools for", "presentation preparation."]}
        emphasis="Free, self-serve resources you can use right now."
        emphasisAccent="underline"
        intro="Quick, concrete tools to help you prepare for a specific presentation — no account, no cost."
      />
      <section className="relative z-10 py-20 md:py-28" data-testid="tools-list-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <ul className="grid md:grid-cols-2 gap-8 md:gap-10" data-testid="tools-grid">
            {TOOLS.map((tool) => (
              <li key={tool.id}>
                <Reveal>
                  <Link
                    to={l(tool.to)}
                    data-testid={`tool-card-${tool.id}`}
                    onClick={() => trackCta({ ctaId: `tool-card-${tool.id}`, location: "list_card", href: tool.to, contentId: tool.id })}
                    className="group block bg-paper border border-ink/12 shadow-[4px_8px_24px_rgba(27,27,24,0.06)] hover:shadow-[6px_12px_36px_rgba(27,27,24,0.12)] transition-shadow duration-500 p-7 md:p-8"
                  >
                    <span
                      data-testid={`tool-card-${tool.id}-badge`}
                      className="inline-block font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3"
                    >
                      {t("tools.badge_free")}
                    </span>
                    <h2 className="font-serif text-2xl md:text-3xl leading-tight text-ink group-hover:text-terracotta transition-colors">
                      {t(tool.titleKey)}
                    </h2>
                    <p className="mt-4 font-body text-ink-soft leading-relaxed">{t(tool.descriptionKey)}</p>
                    <span className="mt-5 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] text-ink">
                      {t("tools.cta_label")} <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                    </span>
                  </Link>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}

// -----------------------------------------------------------------------------
// Guides — a small, static, reusable card list (same pattern as Tools
// above), starting with the Presenter Playbook. Each guide is a free,
// in-depth worksheet tied to one of the diagnostics above — not a blog
// post, something people actually use while preparing.
// -----------------------------------------------------------------------------
const GUIDES = [
  {
    id: "next-presentation-playbook",
    titleKey: "guides.playbook_title",
    descriptionKey: "guides.playbook_description",
    to: ROUTES.presenterPlaybookGuide,
  },
];

export function GuidesList() {
  const { t, l } = useLang();

  return (
    <>
      <PageMeta titleKey="meta.guides_title" descriptionKey="meta.guides_description" path={ROUTES.guides} />
      <PageHero
        testId="guides-hero"
        eyebrow="Insights"
        headline={["Guides worth keeping", "open while you work."]}
        emphasis="Free worksheets, built to be used, not just read."
        emphasisAccent="underline"
        intro="Practical guides on high-stakes communication, each paired with a free diagnostic above."
      />
      <section className="relative z-10 py-20 md:py-28" data-testid="guides-list-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <ul className="grid md:grid-cols-2 gap-8 md:gap-10" data-testid="guides-grid">
            {GUIDES.map((guide) => (
              <li key={guide.id}>
                <Reveal>
                  <Link
                    to={l(guide.to)}
                    data-testid={`guide-card-${guide.id}`}
                    onClick={() => trackCta({ ctaId: `guide-card-${guide.id}`, location: "list_card", href: guide.to, contentId: guide.id })}
                    className="group block bg-paper border border-ink/12 shadow-[4px_8px_24px_rgba(27,27,24,0.06)] hover:shadow-[6px_12px_36px_rgba(27,27,24,0.12)] transition-shadow duration-500 p-7 md:p-8"
                  >
                    <span
                      data-testid={`guide-card-${guide.id}-badge`}
                      className="inline-block font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3"
                    >
                      {t("tools.badge_free")}
                    </span>
                    <h2 className="font-serif text-2xl md:text-3xl leading-tight text-ink group-hover:text-terracotta transition-colors">
                      {t(guide.titleKey)}
                    </h2>
                    <p className="mt-4 font-body text-ink-soft leading-relaxed">{t(guide.descriptionKey)}</p>
                    <span className="mt-5 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] text-ink">
                      {t("tools.cta_label")} <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                    </span>
                  </Link>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}

// -----------------------------------------------------------------------------
// Article detail
// -----------------------------------------------------------------------------
// Articles that get the inline "take the free diagnostic" promo card —
// a deliberate allow-list (not every article), since this recommendation
// is only genuinely relevant to high-stakes presentation prep content.
const DIAGNOSTIC_CTA_SLUGS = [
  "how-to-prepare-for-a-board-presentation",
  "how-to-prepare-an-investor-presentation",
  "how-to-prepare-a-stakeholder-presentation",
  "how-to-prepare-a-sales-presentation",
  "how-to-prepare-a-team-alignment-presentation",
  "how-to-prepare-a-keynote-presentation",
  "how-to-prepare-a-client-presentation",
  "how-to-prepare-a-leadership-presentation",
];

export function ArticleDetail() {
  const { slug } = useParams();
  const { l, lang } = useLang();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [related, setRelated] = useState(null);

  useEffect(() => {
    api.getArticleBySlug(slug)
      .then(setDoc)
      .catch((e) => setErr(e.status === 404 ? "notfound" : e.message));
  }, [slug]);

  useEffect(() => {
    setRelated(null);
    if (!doc?.related_article_slug) return;
    // Resolved at render time via the same public slug endpoint — if the
    // target has since been unpublished or deleted this simply fails
    // silently and the "Related reading" section is skipped.
    api.getArticleBySlug(doc.related_article_slug)
      .then(setRelated)
      .catch(() => setRelated(null));
  }, [doc?.related_article_slug]);

  if (err === "notfound") {
    return (
      <section className="min-h-[60vh] flex items-center justify-center px-5 py-24">
        <div className="text-center max-w-md">
          <p className="font-hand text-terracotta text-2xl">404</p>
          <h1 className="font-serif text-3xl text-ink mt-2">Article not found.</h1>
          <Link to={l(ROUTES.articles)} className="mt-6 inline-block font-body text-sm uppercase tracking-[0.14em] text-terracotta hover:underline">
            ← Back to Insights
          </Link>
        </div>
      </section>
    );
  }
  if (!doc) return <section className="min-h-[60vh] flex items-center justify-center"><p className="font-body text-ink-soft">Loading…</p></section>;

  const title = pick(doc.title, lang);
  const excerpt = pick(doc.excerpt, lang);
  const body = pick(doc.content, lang);
  const seoTitle = pick(doc.seo_title, lang) || title;
  const meta = pick(doc.meta_description, lang) || excerpt;

  return (
    <>
      {/* SEO */}
      <SEO title={seoTitle} description={meta} />

      <article className="relative z-10 pt-32 md:pt-40 pb-24 md:pb-32" data-testid="article-detail">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <Link to={l(ROUTES.articles)} className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-terracotta" data-testid="article-back">
            ← Insights
          </Link>
          {doc.category && <p className="mt-8 font-body text-xs uppercase tracking-[0.24em] text-terracotta">{doc.category}</p>}
          <h1 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
            {title}
          </h1>
          {excerpt && <p className="mt-6 font-serif italic text-xl md:text-2xl text-ink-soft leading-snug max-w-2xl">{excerpt}</p>}
          {doc.author_name && (
            <p className="mt-6 font-body text-sm text-ink-soft" data-testid="article-author">
              <span className="text-ink font-medium">{doc.author_name}</span>
              {doc.author_role && <span> · {doc.author_role}</span>}
            </p>
          )}
        </div>

        {doc.featured_image && (
          <div className="mt-12 md:mt-16 max-w-[1200px] mx-auto px-5 md:px-10">
            <img src={doc.featured_image} alt="" className="w-full h-auto border border-ink/10" />
          </div>
        )}

        <div className="mt-12 md:mt-16 max-w-[760px] mx-auto px-5 md:px-10">
          <div
            className="prose prose-notebook max-w-none font-body text-ink leading-relaxed prose-headings:font-serif prose-headings:text-ink prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-a:text-terracotta prose-strong:text-ink prose-blockquote:font-serif prose-blockquote:italic prose-blockquote:text-ink prose-blockquote:border-l-terracotta"
            onClick={(e) => {
              // Every plain markdown link inside the article body (the
              // mid-article coaching-page mention, the "Related
              // preparation guides" list, any admin-written diagnostic
              // sentence) is a plain <a>, not a PrimaryCTA/TextCTA — so
              // none of those components' built-in tracking applies.
              // This single delegated handler fires the same `cta_click`
              // shape for ANY in-body link click, tagged with the source
              // article slug + href, so every inline link (regardless of
              // destination) is attributable back to this article.
              const link = e.target.closest("a");
              if (link) {
                trackCta({ ctaId: "article-inline-link", location: "article_inline_text", href: link.getAttribute("href"), articleSlug: doc.slug });
              }
            }}
          >
            <ReactMarkdown>{body}</ReactMarkdown>
          </div>

          {DIAGNOSTIC_CTA_SLUGS.includes(doc.slug) && <ArticleDiagnosticCTA articleSlug={doc.slug} />}

          {(doc.cta_label && doc.cta_href) && (
            <div className="mt-16 pt-10 border-t border-ink/10 flex flex-wrap gap-4">
              <PrimaryCTA href={doc.cta_href} testId="article-cta" location={doc.cta_placement || "article_bottom"} articleSlug={doc.slug} targetAssetId={doc.cta_target_asset_id || null} targetService={doc.cta_target_service || null}>{doc.cta_label}</PrimaryCTA>
              <OutlineCTA href={ROUTES.articles} testId="article-more" location="article_bottom" articleSlug={doc.slug}>More insights</OutlineCTA>
            </div>
          )}

          {related && (
            <div className="mt-10 pt-8 border-t border-ink/10" data-testid="article-related">
              <p className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft mb-3">
                {pick({ en: "Related reading", pt: "Leitura relacionada", el: "Σχετική ανάγνωση" }, lang)}
              </p>
              <Link
                to={l(`/insights/articles/${related.slug}`)}
                data-testid="article-related-link"
                className="group block"
                onClick={() => trackCta({ ctaId: "article-related-link", location: "article_related", href: `/insights/articles/${related.slug}`, articleSlug: doc.slug })}
              >
                <h3 className="font-serif text-xl md:text-2xl text-ink group-hover:text-terracotta transition-colors leading-snug">
                  {pick(related.title, lang)}
                </h3>
                {pick(related.excerpt, lang) && (
                  <p className="mt-2 font-body text-sm text-ink-soft leading-relaxed max-w-xl">{pick(related.excerpt, lang)}</p>
                )}
              </Link>
            </div>
          )}
        </div>
      </article>
    </>
  );
}

// -----------------------------------------------------------------------------
// Case studies list + detail
// -----------------------------------------------------------------------------
export function CaseStudiesList() {
  const { l, lang } = useLang();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.listCases()
      .then((d) => setItems(d.items || []))
      .catch((e) => setErr(e.message || "Failed to load"));
  }, []);

  return (
    <>
      <PageMeta
        titleKey="meta.cases_title"
        descriptionKey="meta.cases_description"
        path={ROUTES.caseStudies}
      />
      <PageHero
        testId="cases-hero"
        eyebrow="Client Stories"
        headline={["How the work translates", "into real conversations."]}
        intro="Explore how professionals and teams have used coaching and rehearsal to prepare for important presentations, respond under pressure and communicate more effectively with the people who matter to their work."
      />
      <section className="relative z-10 py-20 md:py-28" data-testid="cases-list-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          {err && <p className="font-body text-terracotta">{err}</p>}
          {items === null && <p className="font-body text-ink-soft italic">Loading…</p>}
          {items && items.length === 0 && (
            <p className="font-serif italic text-2xl text-ink-soft">Case studies are on their way. Come back soon.</p>
          )}
          {items && items.length > 0 && (
            <ul className="grid md:grid-cols-2 gap-8 md:gap-10" data-testid="cases-grid">
              {items.map((c) => (
                <li key={c.id}>
                  <Reveal>
                    <Link
                      to={l(`/insights/case-studies/${c.slug}`)}
                      data-testid={`case-card-${c.slug}`}
                      onClick={() => trackCta({ ctaId: `case-card-${c.slug}`, location: "list_card", href: `/insights/case-studies/${c.slug}`, contentId: c.id || c.slug })}
                      className="group block bg-paper border border-ink/12 shadow-[4px_8px_24px_rgba(27,27,24,0.06)] hover:shadow-[6px_12px_36px_rgba(27,27,24,0.12)] transition-shadow duration-500"
                    >
                      {c.featured_image ? (
                        <div className="aspect-[16/10] overflow-hidden bg-paper-deep border-b border-ink/10">
                          <img src={c.featured_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-[900ms]" />
                        </div>
                      ) : (
                        <div className="aspect-[16/10] bg-paper-deep/60 border-b border-ink/10 flex items-center justify-center">
                          <span className="font-hand text-terracotta text-2xl">Case study</span>
                        </div>
                      )}
                      <div className="p-7 md:p-8">
                        {c.category && (
                          <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-2">
                            {c.category}
                          </p>
                        )}
                        <p className="font-body text-xs uppercase tracking-[0.2em] text-ink-soft mb-3">
                          {c.anonymous ? "Anonymous client" : c.client_name || "Case study"}
                          {c.collaboration_partner && <span className="normal-case tracking-normal"> · with {c.collaboration_partner}</span>}
                        </p>
                        <h2 className="font-serif text-2xl md:text-3xl leading-tight text-ink group-hover:text-terracotta transition-colors">
                          {pick(c.title, lang)}
                        </h2>
                        {pick(c.challenge, lang) && (
                          <p className="mt-4 font-body text-ink-soft leading-relaxed line-clamp-3">{pick(c.challenge, lang)}</p>
                        )}
                        {pick(c.metrics, lang) && (
                          <p className="mt-5 font-hand text-terracotta text-lg">{pick(c.metrics, lang)}</p>
                        )}
                        <span className="mt-5 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] text-ink">
                          Read case study <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

export function CaseStudyDetail() {
  const { slug } = useParams();
  const { l, lang } = useLang();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    api.getCaseBySlug(slug)
      .then(setDoc)
      .catch((e) => setErr(e.status === 404 ? "notfound" : e.message));
  }, [slug]);

  if (err === "notfound") {
    return (
      <section className="min-h-[60vh] flex items-center justify-center px-5 py-24">
        <div className="text-center max-w-md">
          <p className="font-hand text-terracotta text-2xl">404</p>
          <h1 className="font-serif text-3xl text-ink mt-2">Case study not found.</h1>
          <Link to={l(ROUTES.caseStudies)} className="mt-6 inline-block font-body text-sm uppercase tracking-[0.14em] text-terracotta hover:underline">
            ← Back to Case Studies
          </Link>
        </div>
      </section>
    );
  }
  if (!doc) return <section className="min-h-[60vh] flex items-center justify-center"><p className="font-body text-ink-soft">Loading…</p></section>;

  const title = pick(doc.title, lang);
  const challenge = pick(doc.challenge, lang);
  const approach = pick(doc.approach, lang);
  const outcomes = pick(doc.outcomes, lang);
  const metrics = pick(doc.metrics, lang);
  const testimonial = pick(doc.testimonial, lang);

  return (
    <>
      <SEO title={`${title} — Case Study · InterAct Globally`} description={challenge?.slice(0, 155)} />

      <article className="relative z-10 pt-32 md:pt-40 pb-24 md:pb-32" data-testid="case-detail">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10">
          <Link to={l(ROUTES.caseStudies)} className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-terracotta" data-testid="case-back">
            ← Case Studies
          </Link>
          <p className="mt-8 font-body text-xs uppercase tracking-[0.24em] text-terracotta">{doc.category || "Case Study"}</p>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
            {title}
          </h1>
          {doc.collaboration_partner && (
            <p className="mt-4 font-hand text-terracotta text-xl">In collaboration with {doc.collaboration_partner}</p>
          )}
          {!doc.anonymous && doc.client_name && (
            <p className="mt-2 font-body text-ink-soft">Client · {doc.client_name}</p>
          )}
        </div>

        {doc.featured_image && (
          <div className="mt-12 md:mt-16 max-w-[1200px] mx-auto px-5 md:px-10">
            <img src={doc.featured_image} alt="" className="w-full h-auto border border-ink/10" />
          </div>
        )}

        <div className="mt-12 md:mt-16 max-w-[900px] mx-auto px-5 md:px-10 space-y-14">
          {challenge && (
            <section>
              <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">The challenge</p>
              <p className="font-body text-lg text-ink leading-relaxed">{challenge}</p>
            </section>
          )}
          {approach && (
            <section>
              <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">The approach</p>
              <p className="font-body text-lg text-ink leading-relaxed">{approach}</p>
            </section>
          )}
          {outcomes && (
            <section>
              <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">Outcomes</p>
              <div className="prose prose-notebook max-w-none font-body text-ink leading-relaxed">
                <ReactMarkdown>{outcomes}</ReactMarkdown>
              </div>
            </section>
          )}
          {doc.video_vimeo_id && (
            <section data-testid="case-video-section">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">Watch the story</p>
              <div
                className="relative bg-paper border border-ink/12 shadow-[4px_8px_30px_rgba(18,20,24,0.10)] p-5 max-w-sm"
              >
                <div
                  className="relative w-full bg-ink/[0.06] border border-ink/10 overflow-hidden"
                  style={{ paddingBottom: "125%" }}
                >
                  {videoPlaying ? (
                    <iframe
                      src={`https://player.vimeo.com/video/${doc.video_vimeo_id}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&autoplay=1`}
                      title={`${doc.client_name || title} — video`}
                      data-testid="case-video-iframe"
                      className="absolute inset-0 w-full h-full"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setVideoPlaying(true)}
                      data-testid="case-video-play"
                      className="group absolute inset-0 w-full h-full flex items-center justify-center bg-ink/[0.03]"
                      aria-label="Play video"
                    >
                      <span className="flex items-center justify-center w-14 h-14 rounded-full bg-ink text-paper group-hover:bg-terracotta transition-colors duration-300">
                        <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </section>
          )}
          {metrics && (
            <section className="bg-ink text-paper px-6 md:px-10 py-10">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-mustard mb-3">Metrics</p>
              <p className="font-serif text-2xl md:text-3xl italic text-paper leading-snug">{metrics}</p>
            </section>
          )}
          {testimonial && (
            <blockquote className="border-l-2 border-terracotta pl-6 font-serif italic text-2xl md:text-3xl text-ink leading-snug">
              &ldquo;{testimonial}&rdquo;
              {doc.testimonial_author && (
                <p className="mt-4 font-hand text-terracotta text-lg not-italic">— {doc.testimonial_author}</p>
              )}
            </blockquote>
          )}
          {(doc.cta_label && doc.cta_href) && (
            <div className="pt-6 flex flex-wrap gap-4">
              <PrimaryCTA href={doc.cta_href} testId="case-cta" location={doc.cta_placement || "case_study_bottom"} articleSlug={doc.slug} targetAssetId={doc.cta_target_asset_id || null} targetService={doc.cta_target_service || null}>{doc.cta_label}</PrimaryCTA>
              <OutlineCTA href={ROUTES.caseStudies} testId="case-more" location="case_study_bottom" articleSlug={doc.slug}>More case studies</OutlineCTA>
            </div>
          )}
        </div>
      </article>
    </>
  );
}

// Small helper to set title/meta without pulling in another dep.
function SEO({ title, description }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "description");
        document.head.appendChild(el);
      }
      el.setAttribute("content", description);
    }
  }, [title, description]);
  return null;
}
